import React from 'react';
import { Box, Divider, Typography } from '@mui/material';

interface ModalCardProps {
  children: React.ReactNode;
  /** Optional small title bar (with divider) at the top of the card. */
  title?: string;
  /** Optional content rendered below the card, inside the tinted container. */
  footer?: React.ReactNode;
  /** Optional content rendered above the inner card, inside the tinted container (e.g. dialog header with close button). */
  header?: React.ReactNode;
  /** When true, skips the outer flex centering wrapper — for use inside a Dialog which handles centering. */
  dialog?: boolean;
  /** When provided, the card renders as a <form> with this submit handler. */
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
}

/**
 * Reusable centered card layout: tinted rounded container (accentSurface) with
 * a white/dark card inside, centered in the available space. Used by auth views,
 * confirmation screens, and any other modal-style full-page content.
 */
export default function ModalCard({ children, title, footer, header, dialog, onSubmit }: ModalCardProps) {
  const card = (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: header ? 0 : 3,
        width: 530,
        maxWidth: '100%',
        pt: header ? 0 : 1,
        px: 1,
        pb: footer ? 3 : 1,
        backgroundColor: theme.app.accentSurface,
        borderRadius: '24px',
      })}
    >
      {header}
      <Box
        component={onSubmit ? 'form' : 'div'}
        onSubmit={onSubmit}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
          p: 5,
          backgroundColor: theme.app.card,
          borderRadius: '16px',
          mt: header ? 0 : undefined,
        })}
      >
        {title && (
          // Full-bleed title bar: negative margins cancel the card padding.
          <Box sx={{ mt: -5, mx: -5, mb: -1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, px: 5, py: 2.5 }}>
              {title}
            </Typography>
            <Divider />
          </Box>
        )}
        {children}
      </Box>
      {footer}
    </Box>
  );

  if (dialog) return card;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
      }}
    >
      {card}
    </Box>
  );
}
